/**
 * The browser's view of auth: six fetches and no library.
 *
 * better-auth ships a client, but everything the app needs is four endpoints
 * with JSON bodies and a cookie the browser manages itself. Calling them
 * directly keeps the auth surface visible in one readable file, keeps a
 * reactive-store dependency out of the bundle, and makes these functions
 * trivially fake-able in tests. The endpoint *paths* are better-auth's — see
 * `src/worker/auth.ts` for what mounts them.
 *
 * Cookies are `HttpOnly`, so nothing here can read the session; "am I signed
 * in?" is answered by asking the server (`GET /api/me`), which is the only
 * answer that is ever true.
 *
 * The exception, and it is an exception about *rendering* rather than about
 * truth: when the server cannot be reached at all, {@link fetchSession} answers
 * from `identity-cache.ts` and says so on {@link SessionResponse.origin}. See
 * that module's header for why an app whose store is keyed by user id has to
 * know who it is opening for even with no network, and why the cache authorizes
 * nothing.
 */

import type { Entitlement, Plan } from '@/lib/plan';
import { DEFAULT_ENTITLEMENT, PLAN_TIERS, parseEntitlement } from '@/lib/plan';
import type { Profile } from '@/profile/protocol';
import { parseProfile } from '@/profile/protocol';
import { SYNC_PROTOCOL_VERSION } from '@/sync/protocol';
import { clearCachedIdentity, readCachedIdentity, writeCachedIdentity } from './identity-cache';

/** The signed-in user, as `/api/me` returns them. */
export interface AuthUser {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly image: string | null;
  /**
   * `admin`, `user`, or absent (PLAN-OPS Phase 0).
   *
   * Present so the app can *offer* the ops console to the person it belongs to
   * rather than hiding a route they can reach. It is not a permission: the
   * Worker checks the same field on the session for every `/api/admin/*`
   * request, and that check is the boundary. Nothing here is trusted by
   * anything that matters.
   */
  readonly role?: string | null;
}

/**
 * What `/api/me` answers: who, what their plan allows, and what they are called.
 *
 * The entitlement rides along on the session request rather than getting one of
 * its own — the plan changes at exactly the moments the session is re-read, so
 * one fetch keeps both fresh and there is no second cache to invalidate. The
 * profile (PLAN-GALLERY G1) rides along for the same reason.
 */
export interface SessionResponse {
  readonly user: AuthUser | null;
  readonly entitlement: Entitlement;
  /**
   * The display name and avatar this account has claimed, or `null`.
   *
   * `null` for the great majority of accounts — a profile is opt-in — and also
   * for a signed-out visitor, a Worker too old to send the field, and a boot
   * that fell back to the device's cached identity. Every one of those is the
   * same thing to a renderer: no byline, no avatar, initials from nothing.
   *
   * Not cached in `identity-cache.ts`, unlike the entitlement, and the asymmetry
   * is deliberate. The cached plan decides which *limits the UI describes* on a
   * boot with no network; a profile decides nothing at all offline, because the
   * one screen it appears on is the one screen that cannot be used without the
   * server.
   */
  readonly profile: Profile | null;
  /**
   * The admin id behind this session, when an operator started it (PLAN-OPS
   * Phase 4), and `null` for every ordinary sign-in.
   *
   * The server's statement, off `session.impersonated_by`, and the only thing
   * the app shell's impersonation banner is drawn from. Not cached, and
   * deliberately: it is `null` for an offline boot and for a Worker too old to
   * send it, and both of those are safe to be wrong about in the *quiet*
   * direction only — a banner that fails to appear on a plane is a cosmetic
   * miss, while a device that remembered "you are impersonating" from last week
   * would be a lie about a session that has long since expired.
   */
  readonly impersonatedBy: string | null;
  /** Whether the server said this, or the device remembered it. */
  readonly origin: SessionOrigin;
}

/**
 * Where a {@link SessionResponse} came from.
 *
 * `server` means `/api/me` answered — including when it answered "nobody". That
 * is an authoritative statement and the app treats it as one.
 *
 * `device` means it could not be asked: DNS, a dead socket, a timeout, a 5xx, a
 * captive portal. The answer then comes from the identity cache, and the
 * distinction is what lets the session provider render an explicit `offline`
 * state instead of silently demoting a signed-in brewer to a guest with an empty
 * workspace.
 */
export type SessionOrigin = 'server' | 'device';

/** What this deployment supports, from `/api/config`. */
export interface AuthConfig {
  readonly providers: {
    /** False when the Worker has no Google credential; the UI hides the button. */
    readonly google: boolean;
    readonly magicLink: boolean;
    /**
     * Whether the Worker mounts the passkey routes (PLAN-REMAINING P3).
     *
     * True on every deployment running a Worker from P3 onwards — passkeys need
     * no credential to configure, unlike Google — so this is not a deployment
     * switch. What it is is a *version* answer: false means either a Worker from
     * before P3 or a `/api/config` that could not be read, and in both of those
     * the routes may not be there. The sign-in page and the settings section
     * treat it as the first of two gates; the second is whether this browser has
     * WebAuthn at all, which only the browser can say.
     */
    readonly passkey: boolean;
  };
  /** `dev` means magic links are captured locally instead of emailed. */
  readonly emailSender: 'dev' | 'resend';
  /** Whether `/api/dev/emails` exists. */
  readonly devEndpoints: boolean;
  readonly syncProtocolVersion: number;
  /**
   * The assistant (PLAN §7), gated the same way Google sign-in is.
   *
   * `off` means the Worker has no configured AI provider; the drawer renders a
   * setup notice naming the variables instead of a send button that would fail.
   */
  readonly ai: {
    readonly mode: 'live' | 'fake' | 'off';
    /**
     * Assistant messages per calendar month, per tier, on this deployment.
     *
     * Per tier rather than "mine", because the upgrade dialog compares all
     * three and a signed-out visitor sees the same table. What *this* user may
     * spend is `entitlement.aiMonthlyLimit` from `/api/me`.
     */
    readonly monthlyMessageLimits: Readonly<Record<Plan, number>>;
    readonly protocolVersion: number;
  };
  /**
   * Billing (PLAN-LAUNCH B3), gated the same way the assistant is.
   *
   * `enabled: false` means this deployment has no Paddle configuration — every
   * development build, and staging. The upgrade dialog still renders the full
   * comparison; it just has nothing to sell, and says so.
   */
  readonly billing: BillingConfig;
  /**
   * Product analytics, session replay and client-side error tracking
   * (PLAN-REMAINING O1), gated the same way.
   *
   * `null` — every development build, the test suite, and production until the
   * PostHog project exists — means no key, which means `lib/posthog.ts` imports
   * nothing at all. The whole point of putting it here rather than in the
   * bundle is that a deployment without it pays nothing for it, and that a
   * developer's laptop cannot accidentally file events against production.
   */
  readonly analytics: AnalyticsConfig | null;
  /**
   * Brew-day pushes (PLAN-BREW-ALERTS D-BA8), gated the same way.
   *
   * `publicKey: null` — every development build — means this deployment holds no
   * VAPID keypair, so no device can be subscribed here. The brew-day button then
   * asks for notification permission and nothing more, and the Settings row for
   * this device is hidden: an "On" that could never send is worse than an
   * absence.
   */
  readonly push: PushConfig;
}

/** The Paddle price id one paid tier is sold at. Both tiers are yearly only. */
export interface BillingTierPrices {
  readonly yearly: string;
}

/**
 * What the browser needs to open a checkout, or the fact that it cannot.
 *
 * Mirrors `worker/env.ts`'s `BillingConfig`, which is what fills it in on
 * `/api/config`. `null` rather than absent when billing is off, so the client
 * reads one shape on every deployment.
 */
export interface BillingConfig {
  readonly enabled: boolean;
  /** Paddle.js's client-side token. Public by design — see `lib/paddle.ts`. */
  readonly clientToken: string | null;
  readonly prices: Readonly<Record<Exclude<Plan, 'trial'>, BillingTierPrices>> | null;
}

/**
 * Where the browser sends its telemetry, or the fact that it does not.
 *
 * `AnalyticsConfig` in `lib/posthog.ts` is the same shape by design — this is
 * the wire type and that is the loader's argument, and keeping them structurally
 * identical is what lets `startAnalytics(session.config.analytics)` be the whole
 * of the wiring.
 */
export interface AnalyticsConfig {
  /** A PostHog project key: public by design — see `lib/posthog.ts` for why. */
  readonly key: string;
  /** The ingest host, defaulted by the Worker to PostHog's US cloud. */
  readonly host: string;
}

/**
 * The public half of this deployment's VAPID pair, or the fact that it has none.
 *
 * Mirrors `worker/env.ts`'s `PushConfig`, which fills it in, for the reason
 * {@link BillingConfig} mirrors its own: this is the wire type, and the two
 * halves of one field must not be able to drift.
 */
export interface PushConfig {
  /** URL-safe base64 of the raw P-256 point. `null` means push is not set up. */
  readonly publicKey: string | null;
}

/** The config assumed before `/api/config` answers, and if it never does. */
export const UNKNOWN_AUTH_CONFIG: AuthConfig = {
  providers: { google: false, magicLink: true, passkey: false },
  emailSender: 'resend',
  devEndpoints: false,
  syncProtocolVersion: SYNC_PROTOCOL_VERSION,
  ai: {
    mode: 'off',
    monthlyMessageLimits: {
      trial: PLAN_TIERS.trial.aiMonthlyMessages,
      brewmaster: PLAN_TIERS.brewmaster.aiMonthlyMessages,
      beerwright: PLAN_TIERS.beerwright.aiMonthlyMessages,
    },
    protocolVersion: 1,
  },
  billing: { enabled: false, clientToken: null, prices: null },
  analytics: null,
  push: { publicKey: null },
};

/** A call that either worked or has something to show the user. */
export type AuthResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly message: string };

/**
 * How long a boot-time auth request may take before the app gives up on it.
 *
 * The app is local-first: a captive portal that accepts a TCP connection and
 * then never answers must not be able to hold the workspace hostage. Timing out
 * is one of {@link fetchSession}'s "could not be asked" cases, so it means "the
 * identity this device last confirmed, for now" — it used to mean "guest for
 * now", which on a slow network was how a signed-in brewer landed on an empty
 * workspace. The sync client reconnects, and the session provider revalidates,
 * when the network genuinely comes back.
 */
export const AUTH_TIMEOUT_MS = 4000;

/**
 * A `RequestInit` fragment carrying an abort signal, or nothing.
 *
 * Returned as a spreadable object rather than as `AbortSignal | undefined`
 * because `exactOptionalPropertyTypes` distinguishes "absent" from "present and
 * undefined", and `fetch` wants the former.
 */
export function withTimeout(
  ms = AUTH_TIMEOUT_MS,
): Pick<RequestInit, 'signal'> | Record<string, never> {
  if (typeof AbortSignal?.timeout !== 'function') return {};
  return { signal: AbortSignal.timeout(ms) };
}

async function readError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: string; error?: string };
    return body.message ?? body.error ?? `Request failed (${response.status}).`;
  } catch {
    return `Request failed (${response.status}).`;
  }
}

/**
 * The current user and plan. Never throws.
 *
 * **Three outcomes, and telling them apart is the whole point of this
 * function.** It used to have two, and the missing distinction was a bug with a
 * user-visible shape: an unreachable `/api/me` was read as a 401, so a boot with
 * no network — or merely a slow one, past {@link AUTH_TIMEOUT_MS} — opened the
 * *guest* IndexedDB record and showed a brewer an empty workspace.
 *
 * 1. **The server answered.** `origin: 'server'`. A named user is cached for the
 *    next boot; a `user: null` clears the cache, because that is the server
 *    stating the session is over.
 * 2. **The server refused: 401 or 403.** Also authoritative, also clears the
 *    cache, also `origin: 'server'`. This is the path a revoked or expired
 *    session takes, and it must end in guest mode.
 * 3. **The server could not be asked.** `origin: 'device'`, and the answer is
 *    whatever the identity cache holds — which the session provider renders as
 *    `offline` rather than as signed out.
 *
 * Case 3 is deliberately generous about what counts as "could not be asked": a
 * thrown `fetch`, an abort from the timeout, **any non-ok status other than
 * 401/403**, and a 200 whose body is not the JSON this endpoint returns. The
 * last two are not paranoia. A 502 from an edge having a bad minute is not a
 * statement about the session, and a captive portal answers *every* request with
 * `200 text/html` — treating either as "signed out" would wipe the identity of a
 * user whose session is perfectly alive, and drop them onto an empty workspace
 * on the strength of a hotel Wi-Fi splash page.
 */
export async function fetchSession(): Promise<SessionResponse> {
  let response: Response;
  try {
    response = await fetch('/api/me', {
      headers: { accept: 'application/json' },
      ...withTimeout(),
    });
  } catch {
    // Thrown: offline, DNS failure, or the abort from `withTimeout`.
    return unreachable();
  }

  // The only statuses that mean "this browser has no session". Everything else
  // in the 4xx/5xx range means the answer did not arrive.
  if (response.status === 401 || response.status === 403) return signedOut();
  if (!response.ok) return unreachable();

  let body: {
    user?: AuthUser;
    entitlement?: unknown;
    profile?: unknown;
    impersonatedBy?: unknown;
  };
  try {
    body = (await response.json()) as typeof body;
  } catch {
    // A 200 that is not our JSON is not this endpoint answering — see above.
    return unreachable();
  }

  if (body.user === undefined || body.user === null) return signedOut();
  const entitlement = parseEntitlement(body.entitlement);
  writeCachedIdentity(body.user, entitlement);
  // Parsed rather than trusted, exactly like the entitlement above: a Worker
  // from before G1 sends no `profile` at all, and `null` is already the shape
  // "this account has not claimed a name" takes everywhere. The same applies to
  // `impersonatedBy` and a Worker from before Phase 4 — anything that is not a
  // non-empty string reads as "an ordinary session", which is the safe default
  // in the sense that it can only ever *fail* to warn, never invent a warning.
  return {
    user: body.user,
    entitlement,
    profile: parseProfile(body.profile),
    impersonatedBy:
      typeof body.impersonatedBy === 'string' && body.impersonatedBy !== ''
        ? body.impersonatedBy
        : null,
    origin: 'server',
  };
}

/**
 * The server says there is no session: forget the identity and be a guest.
 *
 * Clearing here rather than in the caller is what makes the rule hard to get
 * wrong — every authoritative signed-out answer in {@link fetchSession} goes
 * through this one function.
 */
function signedOut(): SessionResponse {
  clearCachedIdentity();
  return SIGNED_OUT;
}

/**
 * The server could not be asked: fall back to who this device last was.
 *
 * With no cached identity this is indistinguishable from a guest, and reads as
 * one — which is correct, and is exactly M2's behaviour for a browser that has
 * never signed in.
 */
function unreachable(): SessionResponse {
  const cached = readCachedIdentity();
  if (cached === null) {
    return {
      user: null,
      entitlement: DEFAULT_ENTITLEMENT,
      profile: null,
      impersonatedBy: null,
      origin: 'device',
    };
  }
  // No profile offline, and that is the honest answer rather than a gap: it is
  // not cached (see `SessionResponse.profile`), so the device does not know one.
  // Nor is the impersonation flag, for the reason its own doc gives.
  return {
    user: cached.user,
    entitlement: cached.entitlement,
    profile: null,
    impersonatedBy: null,
    origin: 'device',
  };
}

/** The answer for a browser the server has confirmed has no session. */
const SIGNED_OUT: SessionResponse = {
  user: null,
  entitlement: DEFAULT_ENTITLEMENT,
  profile: null,
  impersonatedBy: null,
  origin: 'server',
};

/**
 * Move the signed-in user between plans (dev builds only).
 *
 * The counterpart of {@link fetchDevMagicLink}: it exists so the trial cap can
 * be exercised without a billing provider. The route is absent from a
 * production build, so this returns `null` there.
 */
export async function setDevPlan(plan: Plan): Promise<Entitlement | null> {
  try {
    const response = await fetch('/api/dev/plan', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ plan }),
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { entitlement?: unknown };
    return parseEntitlement(body.entitlement);
  } catch {
    return null;
  }
}

/**
 * Delete the signed-in account and everything in it (PLAN §8.4).
 *
 * `confirm` must be the account's own email address; the server checks it
 * again. Success means the account is gone *and* the session cookie has been
 * cleared by the same response — the caller still has to wipe local storage,
 * which no server can reach. The identity cache is part of that local storage
 * and is dropped here: an account that no longer exists must not be what the
 * next offline boot opens.
 */
export async function deleteAccount(confirm: string): Promise<AuthResult<null>> {
  try {
    const response = await fetch('/api/account/delete', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ confirm }),
    });
    if (!response.ok) return { ok: false, message: await readError(response) };
    clearCachedIdentity();
    return { ok: true, value: null };
  } catch {
    return { ok: false, message: 'Could not reach the server. Check your connection.' };
  }
}

/** Which sign-in methods this deployment offers. Falls back to the safe default. */
export async function fetchAuthConfig(): Promise<AuthConfig> {
  try {
    const response = await fetch('/api/config', {
      headers: { accept: 'application/json' },
      ...withTimeout(),
    });
    if (!response.ok) return UNKNOWN_AUTH_CONFIG;
    const body = (await response.json()) as Partial<AuthConfig>;
    // `ai` arrived in M5, `billing` in M7 and `analytics` in PLAN-REMAINING O1
    // (where it replaced E3's `sentry` block); a browser holding an older
    // cached bundle against a newer Worker (or the reverse) gets the safe
    // default rather than a crash. "Safe" for billing means *off*: a client
    // that cannot tell whether checkout exists must not offer it. "Safe" for
    // analytics means off too, for a smaller reason — a Worker that does not
    // send the block has no key to send, and silence is the right default for
    // telemetry in a way it would not be for, say, sign-in.
    return {
      ...UNKNOWN_AUTH_CONFIG,
      ...body,
      // `providers` is spread key by key rather than taken whole, for the same
      // reason the three blocks below are defaulted: a Worker from before P3
      // sends a `providers` object with no `passkey` in it, and a whole-object
      // replace would leave the field `undefined` — which is not `false`, and
      // would put a passkey button on a page whose server has no route for it.
      providers: { ...UNKNOWN_AUTH_CONFIG.providers, ...body.providers },
      ai: body.ai ?? UNKNOWN_AUTH_CONFIG.ai,
      billing: body.billing ?? UNKNOWN_AUTH_CONFIG.billing,
      analytics: body.analytics ?? null,
      // And `push` in PLAN-BREW-ALERTS. Safe here means "no key", which is what
      // a Worker from before this feature would have sent had it known: the
      // button asks for permission and offers nothing it cannot deliver.
      push: body.push ?? UNKNOWN_AUTH_CONFIG.push,
    };
  } catch {
    return UNKNOWN_AUTH_CONFIG;
  }
}

// ---------------------------------------------------------------------------
// Billing (PLAN-LAUNCH B3)
// ---------------------------------------------------------------------------

/** Where `POST /api/billing/portal` sends the user. */
export interface PortalLinks {
  readonly url: string;
  /**
   * The deep link that cancels their subscription, when Paddle returned one.
   *
   * **Carried, not rendered.** Nothing in the app reads this yet: Settings sends
   * a brewer to `url`, the portal's own overview, which offers cancelling among
   * everything else. It is plumbed from `worker/billing.ts` through here for a
   * "Cancel your plan" affordance that has not been designed, and it stays
   * because dropping it means editing the worker for no change a brewer could
   * see (PLAN-UX-PASS F7).
   */
  readonly cancelUrl?: string;
}

/**
 * Mint a Paddle customer-portal link for the signed-in user.
 *
 * The message on failure is the one shown in a toast, so the two the server can
 * actually produce are translated here rather than leaking an error code into
 * the UI: `no_subscription` means the account has never bought anything, and
 * `billing_not_configured` means this deployment has no Paddle API key. Both are
 * states settings can be in legitimately, which is why neither is an alarm.
 */
export async function openBillingPortal(): Promise<AuthResult<PortalLinks>> {
  try {
    const response = await fetch('/api/billing/portal', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { error?: string };
      return { ok: false, message: PORTAL_MESSAGES[body.error ?? ''] ?? PORTAL_FALLBACK_MESSAGE };
    }
    const body = (await response.json()) as { url?: string; cancelUrl?: string };
    if (typeof body.url !== 'string') return { ok: false, message: PORTAL_FALLBACK_MESSAGE };
    return {
      ok: true,
      value:
        typeof body.cancelUrl === 'string'
          ? { url: body.url, cancelUrl: body.cancelUrl }
          : { url: body.url },
    };
  } catch {
    return { ok: false, message: 'Could not reach the server. Check your connection.' };
  }
}

const PORTAL_FALLBACK_MESSAGE = 'The billing portal is unavailable right now. Try again shortly.';

const PORTAL_MESSAGES: Readonly<Record<string, string>> = {
  no_subscription: 'There is no subscription on this account yet.',
  billing_not_configured: 'Billing is not configured on this deployment.',
};

/**
 * Spend a trial code (PLAN-LAUNCH B5).
 *
 * The server answers one refusal — `invalid_code` — for a code that does not
 * exist, one that is used up, and one this account has already redeemed, and it
 * does that on purpose: there is no rate limit on the endpoint, and telling a
 * guesser that a code is real-but-already-used is telling them it is real. So
 * there is one message here too, and it is the plain one a person who mistyped
 * a code needs rather than a diagnosis they cannot act on.
 *
 * The exception is `has_subscription` (PLAN-UX-PASS A1), which the server names
 * because it describes the caller's own account rather than the code, and which
 * this function turns into {@link SUBSCRIBED_CODE_MESSAGE}.
 *
 * On success the caller refreshes the session: the plan lives on `/api/me`, and
 * one place owning it is worth the extra round-trip.
 */
export async function redeemCode(code: string): Promise<AuthResult<Entitlement>> {
  try {
    const response = await fetch('/api/billing/redeem', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (!response.ok) {
      // The body is read for one distinction only (PLAN-UX-PASS A1): a 409
      // `has_subscription` is the server declining to overwrite a plan the
      // caller pays for, which is worth explaining. Everything else is the
      // deliberately incurious refusal above. A body that will not parse reads
      // as the incurious one, which is the safe direction.
      const failure = (await response.json().catch(() => ({}))) as { error?: unknown };
      return {
        ok: false,
        message:
          failure.error === 'has_subscription' ? SUBSCRIBED_CODE_MESSAGE : INVALID_CODE_MESSAGE,
      };
    }
    const body = (await response.json()) as { entitlement?: unknown };
    return { ok: true, value: parseEntitlement(body.entitlement) };
  } catch {
    return { ok: false, message: 'Could not reach the server. Check your connection.' };
  }
}

/** The one thing a redeemer is told when a code does not work. */
export const INVALID_CODE_MESSAGE = "That code isn't valid.";

/**
 * What a subscriber is told instead (PLAN-UX-PASS A1).
 *
 * Long enough to carry two facts a one-liner would lose: the code was **not**
 * spent, and the reason we stopped is the subscription rather than anything
 * about the code.
 *
 * **It talks about the subscription, never about the tier**, and that is not a
 * stylistic choice. `subscription.paused` revokes the plan to the free tier and
 * leaves the mirror saying `paused`, so a paused account is `plan: 'trial'` and
 * `subscribed: true` at the same time. "You're already on a paid plan" would
 * contradict the plan row two lines up its own screen. A status this build has
 * never seen can be in the same state for the same reason. What is true of all
 * five refusing cases is that Paddle still has a subscription for the account.
 */
export const SUBSCRIBED_CODE_MESSAGE =
  "Your account still has a subscription with us, so we haven't applied this code. It's still unspent, and we'll sort it out if you ask us on the support page.";

/** How often {@link waitForPlan} re-reads `/api/me`, and how long it keeps trying. */
export const PLAN_POLL_INTERVAL_MS = 2000;
export const PLAN_POLL_TIMEOUT_MS = 60_000;

/**
 * Wait for the server to agree that the user is on a plan.
 *
 * Checkout completing and the entitlement changing are two different events:
 * Paddle tells the browser the payment succeeded, and then — separately, over
 * its own retrying webhook delivery — tells the Worker, which is the only thing
 * that may write an `entitlement` row. Usually a second or two apart, sometimes
 * more. So the client polls the one endpoint that already reports the plan
 * rather than inventing an optimistic local upgrade that a refresh would undo.
 *
 * Returns `false` on timeout, which is not a failed payment and must not be
 * described as one: the webhook is still coming, and the plan will be there on
 * the next load. A minute is long enough that hitting the cap is genuinely rare
 * and short enough that nobody watches a spinner forever.
 *
 * @param plan - The tier that was just bought.
 * @param signal - Cancels the wait — a dialog that closed, a component that
 *   unmounted. Resolves `false` rather than throwing.
 */
export async function waitForPlan(plan: Plan, signal?: AbortSignal): Promise<boolean> {
  const deadline = Date.now() + PLAN_POLL_TIMEOUT_MS;
  for (;;) {
    if (signal?.aborted === true) return false;
    const session = await fetchSession();
    if (session.entitlement.plan === plan) return true;
    if (Date.now() >= deadline) return false;
    await sleep(PLAN_POLL_INTERVAL_MS);
  }
}

/**
 * {@link waitForPlan}, for a purchase whose tier this client never chose.
 *
 * A checkout resumed from a Paddle recovery email (`components/paddle-recovery.tsx`)
 * knows only the transaction id; the tier lives inside the transaction, on
 * Paddle's side. What this client *can* know is the plan it is on now, so it
 * waits for that to stop being true. Same polling, same timeout, and the same
 * meaning for `false`: our patience ran out, not the payment.
 *
 * @param from - The plan the account was on when the payment completed.
 */
export async function waitForPlanChange(from: Plan, signal?: AbortSignal): Promise<boolean> {
  const deadline = Date.now() + PLAN_POLL_TIMEOUT_MS;
  for (;;) {
    if (signal?.aborted === true) return false;
    const session = await fetchSession();
    if (session.entitlement.plan !== from) return true;
    if (Date.now() >= deadline) return false;
    await sleep(PLAN_POLL_INTERVAL_MS);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Ask for a magic link.
 *
 * Success means "the server accepted the request", not "the mail arrived" —
 * deliberately, because telling the caller whether the address exists would
 * make this an account-enumeration oracle.
 */
export async function sendMagicLink(email: string, callbackURL = '/'): Promise<AuthResult<null>> {
  try {
    const response = await fetch('/api/auth/sign-in/magic-link', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, callbackURL }),
    });
    if (!response.ok) return { ok: false, message: await readError(response) };
    return { ok: true, value: null };
  } catch {
    return { ok: false, message: 'Could not reach the server. Check your connection.' };
  }
}

/**
 * Start the Google flow.
 *
 * Returns the URL to send the browser to rather than navigating, so the caller
 * decides — and so the function is testable without a real `location`.
 */
export async function startGoogleSignIn(callbackURL = '/'): Promise<AuthResult<string>> {
  try {
    const response = await fetch('/api/auth/sign-in/social', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ provider: 'google', callbackURL }),
    });
    if (!response.ok) return { ok: false, message: await readError(response) };
    const body = (await response.json()) as { url?: string };
    if (typeof body.url !== 'string') {
      return { ok: false, message: 'Google sign-in is not configured on this server.' };
    }
    return { ok: true, value: body.url };
  } catch {
    return { ok: false, message: 'Could not reach the server. Check your connection.' };
  }
}

/**
 * End the session. The server clears the cookie; this clears the local hint.
 *
 * Only on success, and the asymmetry is deliberate: a sign-out that could not
 * reach the server has not ended anything, and forgetting the identity anyway
 * would leave a browser that is still signed in looking at the guest workspace
 * until the next `/api/me`. The failure is reported to the caller instead.
 */
export async function signOut(): Promise<AuthResult<null>> {
  try {
    const response = await fetch('/api/auth/sign-out', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    });
    if (!response.ok) return { ok: false, message: await readError(response) };
    clearCachedIdentity();
    return { ok: true, value: null };
  } catch {
    return { ok: false, message: 'Could not reach the server. Check your connection.' };
  }
}

/**
 * End an impersonation and go back to being the operator (PLAN-OPS Phase 4).
 *
 * better-auth's own endpoint, called directly like every other one in this file.
 * It reads the signed `admin_session` cookie the impersonation stashed, deletes
 * the impersonated session and puts the admin's own cookie back — so this is a
 * *swap*, not a sign-out, and the identity cache must not be cleared: the very
 * next `/api/me` names the admin, and clearing would only mean one boot as a
 * guest in between.
 *
 * A refusal is a real state rather than an impossibility: the hour-long
 * impersonation session can outlive the admin session it came from, and then
 * there is nothing to go back to. The caller reports the message and offers a
 * sign-out, which is the honest remaining move.
 */
export async function stopImpersonating(): Promise<AuthResult<null>> {
  try {
    const response = await fetch('/api/auth/admin/stop-impersonating', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    });
    if (!response.ok) return { ok: false, message: await readError(response) };
    return { ok: true, value: null };
  } catch {
    return { ok: false, message: 'Could not reach the server. Check your connection.' };
  }
}

/** One captured dev email. */
export interface DevEmail {
  readonly recipient: string;
  readonly subject: string;
  readonly url: string;
  readonly createdAt: number;
}

/**
 * The most recent magic link the dev sender captured for an address.
 *
 * Only useful when `AuthConfig.devEndpoints` is true. The sign-in page uses it
 * to show the link inline during local development, so the flow can be
 * completed with no mail server — which is the same path the browser-driven
 * verification takes.
 */
export async function fetchDevMagicLink(email: string): Promise<string | null> {
  try {
    const response = await fetch(`/api/dev/emails?email=${encodeURIComponent(email)}`);
    if (!response.ok) return null;
    const body = (await response.json()) as { emails?: DevEmail[] };
    return body.emails?.[0]?.url ?? null;
  } catch {
    return null;
  }
}
